import { CampusPlan, CareType, MatchingProvidersType, UnlimitedType } from '@uplift/api';
import { STUDENT_ONBOARDING_ROUTING_CONFIG } from '@uplift/constants';
import { ProviderMedicalRole } from '@uplift/types';

import { SELF_PAY_ID } from '../../utils';
import {
  Eligibility, EligibilityGuard, StudentOnboardingContextType, StudentOnboardingMachineContext
} from '../types';

type StudentOnboardingRoutingConfig = typeof STUDENT_ONBOARDING_ROUTING_CONFIG;
type Routes = StudentOnboardingRoutingConfig[keyof StudentOnboardingRoutingConfig];

export const syncRouteInvocation = (route: Routes, options?: { replace?: boolean }) => (({
  src: 'syncRouteActor',
  input: { route, replace: options?.replace }
}) as const);

export const getEligibility = (context: Partial<StudentOnboardingMachineContext>): StudentOnboardingContextType['eligibility'] => {
  /**
   * The eligibility for free sessions depends on evaluating the following four conditions:
   * 1. Org has a Flex or Complete plan
   * 2. Whether the student has insurance or not.
   * 3. The type of care the student is referred for.
   * 4. The type of care the organization is sponsoring.
  */

  let sponsoredCareType: CareType = null;

  if (context.orgContract) {
    switch (context.orgContract?.plan?.type) {
      case CampusPlan.lite:
        sponsoredCareType = null;
        break;
      case CampusPlan.flex:
        if (context.orgContract.plan.therapy > 0 && context.orgContract.plan.psychiatry > 0) {
          sponsoredCareType = CareType.therapyAndPsychiatry;
        } else if (context.orgContract.plan.therapy > 0) {
          sponsoredCareType = CareType.therapy;
        } else if (context.orgContract.plan.psychiatry > 0) {
          sponsoredCareType = CareType.psychiatry;
        }
        break;
      case CampusPlan.complete:
        if (context.orgContract.plan.unlimitedType === UnlimitedType.both) {
          sponsoredCareType = CareType.therapyAndPsychiatry;
        } else if (context.orgContract.plan.unlimitedType === UnlimitedType.therapy) {
          sponsoredCareType = CareType.therapy;
        } else if (context.orgContract.plan.unlimitedType === UnlimitedType.psychiatry) {
          sponsoredCareType = CareType.psychiatry;
        }
        break;
      default:
        break;
    }
  }

  const careType = context.careType;

  const psychSponsored = sponsoredCareType === CareType.psychiatry || sponsoredCareType === CareType.therapyAndPsychiatry;
  const therapySponsored = sponsoredCareType === CareType.therapy || sponsoredCareType === CareType.therapyAndPsychiatry;
  const bothSponsored = psychSponsored && therapySponsored;

  const psychFirst = (
    ((psychSponsored || bothSponsored) && (
      careType === CareType.psychiatry ||
      (careType === CareType.therapyAndPsychiatry && !therapySponsored)
    ))
    || (careType === CareType.psychiatry)
  );

  const isPlanLite = context.orgContract?.plan.type === CampusPlan.lite;
  const hasInsurance = context.insuranceId !== SELF_PAY_ID;

  const sponsoredTherapy = !isPlanLite && sponsoredCareType === CareType.therapy;
  const sponsoredPsychiatry = !isPlanLite && sponsoredCareType === CareType.psychiatry;
  const sponsoredBoth = !isPlanLite && sponsoredCareType === CareType.therapyAndPsychiatry;
  const sponsoredTherapyOrBoth = sponsoredTherapy || sponsoredBoth;
  const sponsoredPsychiatryOrBoth = sponsoredPsychiatry || sponsoredBoth;
  const isBookingSponsored = context.medicalRole === ProviderMedicalRole.therapist ? sponsoredTherapyOrBoth : sponsoredPsychiatryOrBoth;

  const referredForTherapyOrBoth = careType === CareType.therapy || careType === CareType.therapyAndPsychiatry;
  const referredForPsychiatryOrBoth = careType === CareType.psychiatry || careType === CareType.therapyAndPsychiatry;
  const referredForBoth = careType === CareType.therapyAndPsychiatry;

  const freeTherapySessions = !isPlanLite && hasInsurance && sponsoredTherapyOrBoth && referredForTherapyOrBoth;
  const firstSixFreeTherapySessions = !isPlanLite && !hasInsurance && sponsoredTherapyOrBoth && referredForTherapyOrBoth;
  const freePsychiatrySessions = !isPlanLite && hasInsurance && sponsoredPsychiatryOrBoth && referredForPsychiatryOrBoth;
  const freePsychiatryConsultation = !isPlanLite && !hasInsurance && sponsoredPsychiatryOrBoth && referredForPsychiatryOrBoth;
  const paidTherapySession = referredForBoth && !sponsoredTherapyOrBoth && psychFirst;
  const paidPsychiatryConsultation = referredForBoth && !sponsoredPsychiatryOrBoth && !psychFirst;
  const freeSessions = freeTherapySessions || freePsychiatrySessions;
  const isSelfPay = !hasInsurance || context.matchingPaymentType === MatchingProvidersType.cashpay;

  const canSkipSecondBooking = !isBookingSponsored && referredForBoth && isSelfPay;

  const eligibility: Eligibility = {
    isStudentSecondBooking: context.isStudentSecondBooking,
    hasInsurance,
    isPlanLite,
    sponsoredTherapy,
    sponsoredPsychiatry,
    sponsoredTherapyOrBoth,
    sponsoredPsychiatryOrBoth,
    sponsoredBoth,
    psychFirst,
    isBookingSponsored,
    referredForTherapyOrBoth,
    referredForPsychiatryOrBoth,
    referredForBoth,
    freeTherapySessions,
    firstSixFreeTherapySessions,
    freePsychiatrySessions,
    freeSessions,
    freePsychiatryConsultation,
    paidPsychiatryConsultation,
    paidTherapySession,
    isSelfPay,
    canSkipSecondBooking,
  };

  return eligibility;
};

export const eligibilityGuard = (fn: EligibilityGuard<StudentOnboardingMachineContext>, debug = true) => ({ context }) => {
  const eligibility = getEligibility(context);
  const res = fn(eligibility, context);

  if (debug) {
    console.log('eligibilityGuard', { fn, eligibility, context, res });
  }
  return res;
};

export const MATCHES_LOADING_DURATION = 3500;
